import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ErreurApi, type GuideDeTache, lire, requetes } from '../api'
import { Icone } from './Icone'

// Pas-à-pas d'une déclaration, dans la fiche de la tâche : le guide de l'entité pour cette
// démarche, écrit pour quelqu'un qui découvre. Chaque étape dit où aller, quoi regarder,
// pourquoi ; on note le chiffre relevé, on coche, et on signale ce qui ne correspondait pas
// (remarque, reprise ensuite dans le guide). Les gestes humains (payer, déclarer, valider,
// signer) restent faits par la personne, hors de l'appli.

type Guide = NonNullable<GuideDeTache['guide']>
type Etape = Guide['etapes'][number]
type Avancee = { fait?: boolean; valeur?: string | null; remarque?: string | null }

export function GuideTache({
  tacheId,
  ecriture,
  libre,
}: {
  tacheId: string
  ecriture: boolean
  /** Tâche sans échéance : on lui associe un guide, avec sa période. */
  libre: boolean
}) {
  const client = useQueryClient()
  const lu = useQuery(requetes.guideTache(tacheId))
  const [erreur, setErreur] = useState<string | null>(null)

  async function envoyer(appel: Promise<GuideDeTache>) {
    setErreur(null)
    try {
      client.setQueryData(requetes.guideTache(tacheId).queryKey, await appel)
      return true
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Enregistrement impossible.')
      return false
    }
  }

  const avancer = (etape: string, a: Avancee) =>
    envoyer(lire(api.taches[':id'].guide.$post({ param: { id: tacheId }, json: { etape, ...a } })))

  if (lu.isPending || !lu.data) return null
  const { guide, disponibles } = lu.data
  if (!guide) {
    if (!ecriture || !libre || disponibles.length === 0) return null
    return (
      <ChoisirGuide
        disponibles={disponibles}
        erreur={erreur}
        onChoisir={(type, periode) =>
          envoyer(
            lire(
              api.taches[':id']['choisir-guide'].$post({
                param: { id: tacheId },
                json: { type, ...(periode && { periode }) },
              }),
            ),
          )
        }
      />
    )
  }
  return <Pas guide={guide} ecriture={ecriture} erreur={erreur} onAvancer={avancer} />
}

function ChoisirGuide({
  disponibles,
  erreur,
  onChoisir,
}: {
  disponibles: GuideDeTache['disponibles']
  erreur: string | null
  onChoisir: (type: string, periode: string) => Promise<boolean>
}) {
  const [type, setType] = useState(disponibles[0]!.type)
  const [periode, setPeriode] = useState('')
  return (
    <section className="carte section guide">
      <div className="section-titre">
        <h2>
          <Icone nom="school" /> Pas à pas
        </h2>
      </div>
      <p className="discret">
        Une démarche guidée existe pour cette entité : l’associer à la tâche affiche chaque étape,
        où aller et quoi regarder.
      </p>
      <div className="guide-choix">
        <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Guide">
          {disponibles.map((d) => (
            <option key={d.id} value={d.type}>
              {d.titre.replace(/\s*\{[a-z_]+\}/g, '').replace(/\s*\(\s*\)/g, '')}
            </option>
          ))}
        </select>
        <input
          value={periode}
          onChange={(e) => setPeriode(e.target.value)}
          placeholder="Période : 2025, 2026-T2…"
          aria-label="Période"
        />
        <button
          type="button"
          className="bouton bouton-principal"
          onClick={() => onChoisir(type, periode.trim())}
        >
          Suivre ce guide
        </button>
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
    </section>
  )
}

function Pas({
  guide,
  ecriture,
  erreur,
  onAvancer,
}: {
  guide: Guide
  ecriture: boolean
  erreur: string | null
  onAvancer: (etape: string, a: Avancee) => Promise<boolean>
}) {
  // Sans choix, la prochaine étape est ouverte ; un toucher ouvre ou replie une autre étape.
  const [choix, setChoix] = useState<string | null | undefined>(undefined)
  const ouverte = choix === undefined ? guide.prochaine : choix
  return (
    <section className="carte section guide" aria-label="Pas à pas">
      <div className="section-titre">
        <h2>
          <Icone nom="school" /> {guide.titre}
        </h2>
        <span className={`badge grand ${guide.termine ? 'ton-ok' : ''}`}>
          {guide.faites} / {guide.total}
        </span>
      </div>
      <p>{guide.objectif}</p>
      {guide.lexique.length > 0 && (
        <details className="guide-lexique">
          <summary>Les mots à connaître</summary>
          <dl>
            {guide.lexique.map((l) => (
              <div key={l.terme}>
                <dt>{l.terme}</dt>
                <dd>{l.sens}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
      <p className="discret guide-claude">
        <Icone nom="smart_toy" /> Avec Claude : « Accompagne-moi pour cette tâche », il suit ce
        guide une étape à la fois et note les chiffres.
      </p>
      {erreur && <p className="alerte">{erreur}</p>}
      <ol className="guide-etapes">
        {guide.etapes.map((e, i) => (
          <EtapeGuide
            key={e.id}
            numero={i + 1}
            etape={e}
            ouverte={ouverte === e.id}
            prochaine={guide.prochaine === e.id}
            ecriture={ecriture}
            onBasculer={() => setChoix(ouverte === e.id ? null : e.id)}
            onAvancer={async (a) => {
              const ok = await onAvancer(e.id, a)
              // Étape cochée : la suivante s'ouvre.
              if (ok && a.fait) setChoix(undefined)
              return ok
            }}
          />
        ))}
      </ol>
    </section>
  )
}

const LIBELLES_STATUT: Record<Etape['statut'], string> = {
  faite: 'Faite',
  a_faire: 'À faire',
  en_attente: 'Selon une réponse plus haut',
  sautee: 'Sans objet ici',
}

function EtapeGuide({
  numero,
  etape: e,
  ouverte,
  prochaine,
  ecriture,
  onBasculer,
  onAvancer,
}: {
  numero: number
  etape: Etape
  ouverte: boolean
  prochaine: boolean
  ecriture: boolean
  onBasculer: () => void
  onAvancer: (a: Avancee) => Promise<boolean>
}) {
  const faite = e.statut === 'faite'
  const sautee = e.statut === 'sautee'
  const visible = ouverte
  return (
    <li className={`guide-etape statut-${e.statut}${prochaine ? ' prochaine' : ''}`}>
      <button
        type="button"
        className="guide-etape-tete"
        onClick={onBasculer}
        aria-expanded={visible}
      >
        <span className="guide-numero" aria-hidden>
          {faite ? <Icone nom="check" /> : numero}
        </span>
        <span className="guide-etape-titre">{e.titre}</span>
        {e.humain && !faite && !sautee && (
          <span className="badge ton-ambre">
            <Icone nom="front_hand" /> Vous-même
          </span>
        )}
        {e.valeurLue && <span className="badge mono">{e.valeurLue}</span>}
        {(sautee || e.statut === 'en_attente') && (
          <span className="badge">{LIBELLES_STATUT[e.statut]}</span>
        )}
        {e.etat?.remarque && (
          <span className="badge ton-ambre" title="Remarque">
            <Icone nom="warning" />
          </span>
        )}
      </button>
      {visible && <CorpsEtape etape={e} ecriture={ecriture} onAvancer={onAvancer} />}
    </li>
  )
}

function CorpsEtape({
  etape: e,
  ecriture,
  onAvancer,
}: {
  etape: Etape
  ecriture: boolean
  onAvancer: (a: Avancee) => Promise<boolean>
}) {
  const [remarque, setRemarque] = useState(e.etat?.remarque ?? '')
  const [remarqueOuverte, setRemarqueOuverte] = useState(false)
  const faite = e.statut === 'faite'
  return (
    <div className="guide-etape-corps">
      {e.ou && (
        <p className="guide-ou">
          <Icone nom="open_in_new" />
          <span>
            <strong>Où : </strong>
            {e.ou.adresse ? (
              <a href={e.ou.adresse} target="_blank" rel="noreferrer">
                {e.ou.site}
              </a>
            ) : (
              e.ou.site
            )}
            {e.ou.chemin && <span className="guide-chemin"> {e.ou.chemin}</span>}
          </span>
        </p>
      )}
      <p>{e.quoi}</p>
      {e.pourquoi && <p className="discret">Pourquoi : {e.pourquoi}</p>}
      {e.humain && (
        <p className="guide-humain">
          <Icone nom="front_hand" /> C’est un geste à faire vous-même : l’appli et l’assistant ne le
          font pas à votre place.
        </p>
      )}
      {e.releve && <Releve etape={e} ecriture={ecriture} onAvancer={onAvancer} />}
      {e.preuve && (
        <p className="discret">
          <Icone nom="attach_file" /> À garder : {e.preuve}
        </p>
      )}
      {e.siBloque && (
        <details className="guide-bloque">
          <summary>Si l’écran ne ressemble pas</summary>
          <p>{e.siBloque}</p>
        </details>
      )}
      {e.etat?.remarque && !remarqueOuverte && (
        <p className="guide-remarque">
          <Icone nom="warning" /> Remarque : {e.etat.remarque}
        </p>
      )}
      {ecriture && (
        <div className="guide-actions">
          <button
            type="button"
            className={faite ? 'bouton-discret' : 'bouton bouton-principal'}
            onClick={() => onAvancer({ fait: !faite })}
          >
            {faite ? 'Rouvrir l’étape' : 'C’est fait'}
          </button>
          {!remarqueOuverte && (
            <button
              type="button"
              className="bouton-discret"
              onClick={() => setRemarqueOuverte(true)}
            >
              Ce n’était pas comme ça
            </button>
          )}
        </div>
      )}
      {remarqueOuverte && (
        <div className="guide-remarque-saisie">
          <textarea
            value={remarque}
            onChange={(ev) => setRemarque(ev.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Ce qui était réellement affiché, et où : le guide sera corrigé."
          />
          <div className="guide-actions">
            <button
              type="button"
              className="bouton"
              onClick={async () => {
                if (await onAvancer({ remarque: remarque.trim() || null }))
                  setRemarqueOuverte(false)
              }}
            >
              Enregistrer la remarque
            </button>
            <button
              type="button"
              className="bouton-discret"
              onClick={() => setRemarqueOuverte(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Releve({
  etape: e,
  ecriture,
  onAvancer,
}: {
  etape: Etape
  ecriture: boolean
  onAvancer: (a: Avancee) => Promise<boolean>
}) {
  const r = e.releve!
  const [saisie, setSaisie] = useState(e.valeurLue ?? '')
  return (
    <div className="guide-releve">
      <label>
        <strong>À noter : {r.libelle}</strong>
        {r.aide && <span className="discret"> {r.aide}</span>}
      </label>
      {r.nature === 'oui_non' ? (
        <div className="guide-actions">
          {(['oui', 'non'] as const).map((v) => (
            <button
              key={v}
              type="button"
              disabled={!ecriture}
              className={e.etat?.valeur === v ? 'bouton bouton-principal' : 'bouton'}
              onClick={() => onAvancer({ valeur: v })}
            >
              {v === 'oui' ? 'Oui' : 'Non'}
            </button>
          ))}
        </div>
      ) : (
        <form
          className="guide-actions"
          onSubmit={(ev) => {
            ev.preventDefault()
            void onAvancer({ valeur: saisie.trim() || null })
          }}
        >
          <input
            value={saisie}
            onChange={(ev) => setSaisie(ev.target.value)}
            disabled={!ecriture}
            inputMode={r.nature === 'montant' ? 'decimal' : undefined}
            placeholder={
              r.nature === 'montant' ? '1 234,56' : r.nature === 'date' ? '15/09/2026' : ''
            }
            aria-label={r.libelle}
          />
          {ecriture && (
            <button type="submit" className="bouton">
              Noter
            </button>
          )}
        </form>
      )}
    </div>
  )
}
